// Manager/Admin "Water Restriction Compliance Report" — an ACTIONABLE report that
// tells managers exactly WHAT to fix (which controller, which days) and WHERE the
// hotspots are (which cities), not just a score. Pure read surface over
// GET /api/dashboard/compliance, classified client-side by the shared compliance
// engine in lib/waterRestrictions. Print- and CSV-export friendly.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  AlertTriangle,
  CalendarX,
  CheckCircle2,
  Clock,
  Download,
  Droplets,
  FileWarning,
  HelpCircle,
  Lightbulb,
  MapPin,
  Printer,
  Search,
  ShieldCheck,
  ShieldQuestion,
  X,
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import {
  Card,
  Donut,
  Legend,
  BarList,
  StatTile,
  Pill,
  type DonutSegment,
  type BarDatum,
  type Tone,
} from './dashboard/charts';
import {
  evaluatePropertyComplianceDetailed,
  formatDays,
  WATER_CLASS_LABELS,
  type WaterRestrictionSchedule,
  type ComplianceStatus,
  type DetailedComplianceResult,
  type ViolationKind,
  type Violation,
  type WaterClass,
} from '../lib/waterRestrictions';

// ---------------------------------------------------------------------------
// Endpoint shapes
// ---------------------------------------------------------------------------
interface CityPropertyRow {
  id: string;
  name: string;
  address: string | null;
  water_restriction_class: 'residential' | 'non_residential' | null;
  water_restriction_override: unknown;
  controllers: Array<{
    id: string;
    controller_number: number;
    description: string | null;
    location: string | null;
    model: string | null;
    water_start_times: unknown;
    water_restriction_override: { acknowledged?: boolean } | null;
  }>;
}

interface ComplianceCity {
  city: string;
  schedule: WaterRestrictionSchedule | null;
  properties: CityPropertyRow[];
}

interface ComplianceResponse {
  cities: ComplianceCity[];
}

// A property after running it through the detailed compliance engine, carrying
// the city + raw row so the report and the detail modal can render everything.
interface EvaluatedProperty {
  id: string;
  name: string;
  address: string | null;
  city: string;
  cityHasSchedule: boolean;
  cls: WaterClass | null;
  result: DetailedComplianceResult;
}

// One actionable row: a non-compliant property × a violating controller.
interface ActionRow {
  property: EvaluatedProperty;
  controllerLabel: string;
  acknowledged: boolean;
  violation: Violation;
}

const STATUS_COLORS: Record<ComplianceStatus, string> = {
  compliant: '#059669',
  non_compliant: '#dc2626',
  undetermined: '#f59e0b',
  no_schedule: '#9ca3af',
};

const STATUS_TONE: Record<ComplianceStatus, Tone> = {
  compliant: 'emerald',
  non_compliant: 'red',
  undetermined: 'amber',
  no_schedule: 'gray',
};

const VIOLATION_KINDS: ViolationKind[] = ['day', 'time', 'max_days'];

export function ComplianceReport() {
  const { t } = useTranslation('common');

  const [cities, setCities] = useState<ComplianceCity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // action-list filters
  const [cityFilter, setCityFilter] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<ViolationKind | null>(null);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState<EvaluatedProperty | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .get<ComplianceResponse>('/api/dashboard/compliance')
      .then((res) => {
        if (cancelled) return;
        setCities(res.cities ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // -------------------------------------------------------------------------
  // Evaluate every property once.
  // -------------------------------------------------------------------------
  const evaluated = useMemo<EvaluatedProperty[]>(() => {
    const out: EvaluatedProperty[] = [];
    for (const city of cities) {
      for (const p of city.properties) {
        const result = evaluatePropertyComplianceDetailed(city.schedule, {
          id: p.id,
          name: p.name,
          address: p.address,
          city: city.city,
          water_restriction_class: p.water_restriction_class,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          water_restriction_override: p.water_restriction_override as any,
          controllers: p.controllers.map((c) => ({
            id: c.id,
            controller_number: c.controller_number,
            description: c.description,
            location: c.location,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            water_start_times: c.water_start_times as any,
            water_restriction_override: c.water_restriction_override,
          })),
        });
        out.push({
          id: p.id,
          name: p.name,
          address: p.address,
          city: city.city,
          cityHasSchedule: city.schedule != null,
          cls: p.water_restriction_class,
          result,
        });
      }
    }
    return out;
  }, [cities]);

  // -------------------------------------------------------------------------
  // Score strip aggregates.
  // -------------------------------------------------------------------------
  const totals = useMemo(() => {
    const byStatus: Record<ComplianceStatus, number> = {
      compliant: 0,
      non_compliant: 0,
      undetermined: 0,
      no_schedule: 0,
    };
    const kinds: Record<ViolationKind, number> = { day: 0, time: 0, max_days: 0 };
    let overridesOutstanding = 0; // controllers with violations that were acknowledged
    for (const e of evaluated) {
      byStatus[e.result.status] += 1;
      kinds.day += e.result.kinds.day;
      kinds.time += e.result.kinds.time;
      kinds.max_days += e.result.kinds.max_days;
      for (const cd of e.result.controllerDetails) {
        if (cd.acknowledged && cd.violations.length > 0) overridesOutstanding += 1;
      }
    }
    const total = evaluated.length;
    // Denominator for the compliant rate excludes no_schedule (can't grade them).
    const graded = total - byStatus.no_schedule;
    const compliantPct = graded > 0 ? Math.round((byStatus.compliant / graded) * 100) : 0;
    const coveragePct = total > 0 ? Math.round(((total - byStatus.no_schedule) / total) * 100) : 0;
    return { byStatus, kinds, overridesOutstanding, total, graded, compliantPct, coveragePct };
  }, [evaluated]);

  // -------------------------------------------------------------------------
  // Hotspots by city — non-compliance rate.
  // -------------------------------------------------------------------------
  const cityRows = useMemo(() => {
    const map = new Map<
      string,
      { city: string; hasSchedule: boolean; total: number; graded: number; nonCompliant: number }
    >();
    for (const e of evaluated) {
      let row = map.get(e.city);
      if (!row) {
        row = { city: e.city, hasSchedule: e.cityHasSchedule, total: 0, graded: 0, nonCompliant: 0 };
        map.set(e.city, row);
      }
      row.total += 1;
      if (e.result.status !== 'no_schedule') row.graded += 1;
      if (e.result.status === 'non_compliant') row.nonCompliant += 1;
    }
    return Array.from(map.values())
      .map((r) => ({ ...r, rate: r.graded > 0 ? Math.round((r.nonCompliant / r.graded) * 100) : 0 }))
      .sort((a, b) => b.nonCompliant - a.nonCompliant || b.rate - a.rate);
  }, [evaluated]);

  const noScheduleCities = useMemo(() => cityRows.filter((c) => !c.hasSchedule), [cityRows]);

  // -------------------------------------------------------------------------
  // Action list — one row per (non-compliant property × violating controller).
  // -------------------------------------------------------------------------
  const allActionRows = useMemo<ActionRow[]>(() => {
    const rows: ActionRow[] = [];
    for (const e of evaluated) {
      if (e.result.status !== 'non_compliant') continue;
      for (const cd of e.result.controllerDetails) {
        for (const v of cd.violations) {
          rows.push({
            property: e,
            controllerLabel: cd.controllerLabel,
            acknowledged: cd.acknowledged,
            violation: v,
          });
        }
      }
    }
    // worst-first: unacknowledged before acknowledged, then by property name.
    rows.sort((a, b) => {
      if (a.acknowledged !== b.acknowledged) return a.acknowledged ? 1 : -1;
      return a.property.name.localeCompare(b.property.name);
    });
    return rows;
  }, [evaluated]);

  const actionRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allActionRows.filter((r) => {
      if (cityFilter && r.property.city !== cityFilter) return false;
      if (kindFilter && r.violation.kind !== kindFilter) return false;
      if (q && !r.property.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allActionRows, cityFilter, kindFilter, search]);

  // -------------------------------------------------------------------------
  // Coverage gaps.
  // -------------------------------------------------------------------------
  const coverage = useMemo(() => {
    // (b) property has a rule but no controller has a programmed schedule.
    const noControllerSchedule = evaluated.filter(
      (e) => e.result.status === 'no_schedule' && e.cityHasSchedule,
    );
    // (c) undetermined — rule exists, clean on what we could check, day rule N/A.
    const undetermined = evaluated.filter((e) => e.result.status === 'undetermined');
    return { noScheduleCities, noControllerSchedule, undetermined };
  }, [evaluated, noScheduleCities]);

  // -------------------------------------------------------------------------
  // Recommendations — generated from the computed data, top 5.
  // -------------------------------------------------------------------------
  const recommendations = useMemo<string[]>(() => {
    const recs: string[] = [];

    if (noScheduleCities.length > 0) {
      const names = noScheduleCities.map((c) => c.city).slice(0, 6).join(', ');
      recs.push(
        t('complianceReport.recDefineSchedules', {
          count: noScheduleCities.length,
          cities: names,
          defaultValue: `Define a water schedule for {{count}} cities ({{cities}}).`,
        }),
      );
    }

    const unack = allActionRows.filter((r) => !r.acknowledged);
    if (unack.length > 0) {
      const props = new Set(unack.map((r) => r.property.id));
      recs.push(
        t('complianceReport.recResolveViolations', {
          count: unack.length,
          properties: props.size,
          defaultValue: `Resolve {{count}} unacknowledged violations across {{properties}} properties.`,
        }),
      );
    }

    // Pick the single worst day-violation to reprogram (most actionable, concrete).
    const dayRow = allActionRows.find((r) => !r.acknowledged && r.violation.kind === 'day');
    if (dayRow) {
      recs.push(
        t('complianceReport.recReprogram', {
          controller: dayRow.controllerLabel,
          property: dayRow.property.name,
          allowedDays: formatDays(dayRow.property.result.allowedDays),
          defaultValue: `Reprogram {{controller}} at {{property}} to water only {{allowedDays}}.`,
        }),
      );
    }

    if (coverage.noControllerSchedule.length > 0) {
      recs.push(
        t('complianceReport.recProgramControllers', {
          count: coverage.noControllerSchedule.length,
          defaultValue: `Capture programmed schedules for {{count}} properties with a rule but no controller schedule on file — they can't be verified.`,
        }),
      );
    }

    if (coverage.undetermined.length > 0) {
      recs.push(
        t('complianceReport.recUndetermined', {
          count: coverage.undetermined.length,
          defaultValue: `Add street numbers / confirm rules for {{count}} undetermined properties so day compliance can be checked.`,
        }),
      );
    }

    if (totals.overridesOutstanding > 0) {
      recs.push(
        t('complianceReport.recReviewOverrides', {
          count: totals.overridesOutstanding,
          defaultValue: `Review {{count}} acknowledged overrides — they remain technically non-compliant.`,
        }),
      );
    }

    return recs.slice(0, 5);
  }, [allActionRows, noScheduleCities, coverage, totals.overridesOutstanding, t]);

  // -------------------------------------------------------------------------
  // Human-readable problem text for a violation.
  // -------------------------------------------------------------------------
  function problemText(v: Violation): string {
    switch (v.kind) {
      case 'day':
        return t('complianceReport.problemDay', {
          watered: formatDays(v.days ?? null),
          allowed: formatDays(v.allowed ?? null),
          defaultValue: `Waters on {{watered}} — only {{allowed}} allowed.`,
        });
      case 'time':
        return t('complianceReport.problemTime', {
          window: v.window ?? '',
          defaultValue: `Runs during no-water window {{window}}.`,
        });
      case 'max_days':
        return t('complianceReport.problemMaxDays', {
          count: v.count ?? 0,
          max: v.max ?? 0,
          defaultValue: `Waters {{count}} days/week — max {{max}}.`,
        });
      default:
        return '';
    }
  }

  function kindLabel(k: ViolationKind): string {
    switch (k) {
      case 'day':
        return t('complianceReport.kindDay', 'Wrong day');
      case 'time':
        return t('complianceReport.kindTime', 'No-water hours');
      case 'max_days':
        return t('complianceReport.kindMaxDays', 'Too many days');
    }
  }

  function statusLabel(s: ComplianceStatus): string {
    switch (s) {
      case 'compliant':
        return t('complianceReport.statusCompliant', 'Compliant');
      case 'non_compliant':
        return t('complianceReport.statusNonCompliant', 'Non-compliant');
      case 'undetermined':
        return t('complianceReport.statusUndetermined', "Can't verify");
      case 'no_schedule':
        return t('complianceReport.statusNoSchedule', 'No schedule');
    }
  }

  // -------------------------------------------------------------------------
  // CSV export of the (filtered) action list.
  // -------------------------------------------------------------------------
  function exportCsv() {
    const headers = [
      t('complianceReport.csvProperty', 'Property'),
      t('complianceReport.csvCity', 'City'),
      t('complianceReport.csvController', 'Controller'),
      t('complianceReport.csvViolationType', 'Violation type'),
      t('complianceReport.csvWateredDays', 'Watered days'),
      t('complianceReport.csvAllowedDays', 'Allowed days'),
      t('complianceReport.csvStatus', 'Status'),
    ];
    const esc = (val: string) => `"${(val ?? '').replace(/"/g, '""')}"`;
    const lines = [headers.map(esc).join(',')];
    for (const r of actionRows) {
      const watered =
        r.violation.kind === 'day' ? formatDays(r.violation.days ?? null) : '';
      const allowed =
        r.violation.kind === 'day'
          ? formatDays(r.violation.allowed ?? null)
          : formatDays(r.property.result.allowedDays);
      lines.push(
        [
          r.property.name,
          r.property.city,
          r.controllerLabel,
          kindLabel(r.violation.kind),
          watered,
          allowed,
          r.acknowledged
            ? t('complianceReport.acknowledged', 'Acknowledged')
            : t('complianceReport.unaddressed', 'Unaddressed'),
        ]
          .map(esc)
          .join(','),
      );
    }
    const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `water-compliance-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="p-6 text-sm text-theme-secondary">
        {t('complianceReport.loading', 'Loading compliance report…')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-6 flex items-start gap-2 rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">{t('complianceReport.errorTitle', 'Could not load the compliance report')}</p>
          <p className="mt-0.5 text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (evaluated.length === 0) {
    return (
      <div className="m-6 flex flex-col items-center justify-center rounded-xl border border-line bg-surface p-10 text-center shadow-sm">
        <Droplets size={28} className="text-theme-secondary" />
        <p className="mt-2 text-sm font-medium text-theme-primary">
          {t('complianceReport.emptyTitle', 'No properties to evaluate')}
        </p>
        <p className="mt-0.5 text-sm text-theme-secondary">
          {t('complianceReport.emptyHint', 'Once properties have a city and controller schedules, compliance will appear here.')}
        </p>
      </div>
    );
  }

  const donutSegments: DonutSegment[] = (
    ['compliant', 'non_compliant', 'undetermined', 'no_schedule'] as ComplianceStatus[]
  ).map((s) => ({
    key: s,
    label: statusLabel(s),
    value: totals.byStatus[s],
    color: STATUS_COLORS[s],
  }));

  const violationBars: BarDatum[] = VIOLATION_KINDS.map((k) => ({
    key: k,
    label: kindLabel(k),
    value: totals.kinds[k],
    color: k === 'day' ? '#dc2626' : k === 'time' ? '#7c3aed' : '#ea580c',
  }));

  const hotspotBars: BarDatum[] = cityRows
    .filter((c) => c.nonCompliant > 0)
    .slice(0, 12)
    .map((c) => ({
      key: c.city,
      label: c.city,
      value: c.nonCompliant,
      sub: `${c.rate}%`,
      color: c.city === cityFilter ? '#b91c1c' : '#dc2626',
    }));

  const generatedDate = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 print:p-0">
      {/* 1. Header --------------------------------------------------------- */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-theme-primary">
            <Droplets size={22} className="text-sky-600" />
            {t('complianceReport.title', 'Water Restriction Compliance Report')}
          </h1>
          <p className="mt-0.5 text-sm text-theme-secondary">
            {t('complianceReport.generated', { date: generatedDate, defaultValue: `Generated {{date}}` })}
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-theme-secondary shadow-sm hover:bg-subtle"
          >
            <Download size={15} />
            {t('complianceReport.exportCsv', 'Export CSV')}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-theme-secondary shadow-sm hover:bg-subtle"
          >
            <Printer size={15} />
            {t('complianceReport.print', 'Print')}
          </button>
        </div>
      </header>

      {/* 2. Score strip --------------------------------------------------- */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile
          icon={<MapPin size={15} />}
          tone="blue"
          label={t('complianceReport.tileTotal', 'Properties')}
          value={totals.total}
          sub={t('complianceReport.tileCoverage', {
            pct: totals.coveragePct,
            defaultValue: `{{pct}}% with a schedule`,
          })}
        />
        <StatTile
          icon={<ShieldCheck size={15} />}
          tone="emerald"
          label={t('complianceReport.tileCompliant', 'Compliant')}
          value={`${totals.compliantPct}%`}
          sub={t('complianceReport.tileCompliantSub', {
            count: totals.byStatus.compliant,
            graded: totals.graded,
            defaultValue: `{{count}} of {{graded}} graded`,
          })}
        />
        <StatTile
          icon={<AlertTriangle size={15} />}
          tone="red"
          label={t('complianceReport.tileNonCompliant', 'Non-compliant')}
          value={totals.byStatus.non_compliant}
        />
        <StatTile
          icon={<ShieldQuestion size={15} />}
          tone="amber"
          label={t('complianceReport.tileUndetermined', "Can't verify")}
          value={totals.byStatus.undetermined}
        />
        <StatTile
          icon={<FileWarning size={15} />}
          tone="gray"
          label={t('complianceReport.tileNoSchedule', 'Coverage gap')}
          value={totals.byStatus.no_schedule}
        />
        <StatTile
          icon={<ShieldCheck size={15} />}
          tone="violet"
          label={t('complianceReport.tileOverrides', 'Overrides')}
          value={totals.overridesOutstanding}
          sub={t('complianceReport.tileOverridesSub', 'acknowledged violations')}
        />
      </section>

      {/* 3. Compliance breakdown ------------------------------------------ */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title={t('complianceReport.breakdownTitle', 'Compliance breakdown')} icon={<ShieldCheck size={15} />}>
          <div className="flex flex-wrap items-center gap-4">
            <Donut
              segments={donutSegments}
              centerValue={`${totals.compliantPct}%`}
              centerLabel={t('complianceReport.donutCenter', 'compliant')}
            />
            <div className="min-w-[180px] flex-1">
              <Legend segments={donutSegments} total={totals.total} />
            </div>
          </div>
        </Card>
        <Card title={t('complianceReport.byTypeTitle', 'Violations by type')} icon={<AlertTriangle size={15} />}>
          {violationBars.some((b) => b.value > 0) ? (
            <BarList data={violationBars} onSelect={(k) => setKindFilter(k as ViolationKind)} />
          ) : (
            <p className="py-6 text-center text-sm text-theme-secondary">
              {t('complianceReport.noViolations', 'No violations detected.')}
            </p>
          )}
        </Card>
      </section>

      {/* 4. Hotspots by city ---------------------------------------------- */}
      <Card title={t('complianceReport.hotspotsTitle', 'Hotspots by city')} icon={<MapPin size={15} />}>
        {hotspotBars.length > 0 ? (
          <>
            <p className="mb-3 text-xs text-theme-secondary">
              {t('complianceReport.hotspotsHint', 'Cities ranked by non-compliant property count (% of graded properties). Click to filter the action list.')}
            </p>
            <BarList
              data={hotspotBars}
              onSelect={(city) => setCityFilter((cur) => (cur === city ? null : city))}
            />
          </>
        ) : (
          <p className="py-4 text-center text-sm text-theme-secondary">
            {t('complianceReport.noHotspots', 'No non-compliant properties.')}
          </p>
        )}

        {noScheduleCities.length > 0 && (
          <div className="mt-4 rounded-lg border border-line bg-subtle p-3">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-theme-primary">
              <FileWarning size={14} className="text-theme-secondary" />
              {t('complianceReport.coverageGapCities', 'Cities with no rule defined (coverage gap)')}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {noScheduleCities.map((c) => (
                <Pill key={c.city} tone="gray">
                  {c.city} · {c.total}
                </Pill>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* 5. Action list — what to fix ------------------------------------- */}
      <Card
        title={t('complianceReport.actionTitle', 'What to fix')}
        icon={<AlertTriangle size={15} />}
        action={
          <span className="text-xs font-normal text-theme-secondary">
            {t('complianceReport.actionCount', {
              shown: actionRows.length,
              total: allActionRows.length,
              defaultValue: `{{shown}} of {{total}}`,
            })}
          </span>
        }
        bodyClassName="p-0"
      >
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3 print:hidden">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-secondary" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('complianceReport.searchPlaceholder', 'Search property…')}
              className="w-48 rounded-lg border border-line bg-surface text-theme-primary py-1.5 pl-8 pr-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {VIOLATION_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKindFilter((cur) => (cur === k ? null : k))}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                kindFilter === k ? 'bg-blue-600 text-white' : 'bg-subtle text-theme-secondary hover:bg-subtle'
              }`}
            >
              {kindLabel(k)}
            </button>
          ))}

          {(cityFilter || kindFilter || search) && (
            <button
              type="button"
              onClick={() => {
                setCityFilter(null);
                setKindFilter(null);
                setSearch('');
              }}
              className="inline-flex items-center gap-1 rounded-full bg-subtle px-2.5 py-1 text-xs font-medium text-theme-secondary hover:bg-subtle"
            >
              <X size={12} />
              {t('complianceReport.clearFilters', 'Clear')}
              {cityFilter ? ` · ${cityFilter}` : ''}
            </button>
          )}
        </div>

        {actionRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
            <CheckCircle2 size={26} className="text-emerald-500" />
            <p className="mt-2 text-sm font-medium text-theme-primary">
              {allActionRows.length === 0
                ? t('complianceReport.actionAllClear', 'No violations — every controller is compliant.')
                : t('complianceReport.actionNoMatch', 'No violations match the current filters.')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-theme-secondary">
                  <th className="px-4 py-2 font-medium">{t('complianceReport.colProperty', 'Property')}</th>
                  <th className="px-4 py-2 font-medium">{t('complianceReport.colController', 'Controller')}</th>
                  <th className="px-4 py-2 font-medium">{t('complianceReport.colProblem', 'Problem')}</th>
                  <th className="px-4 py-2 font-medium">{t('complianceReport.colStatus', 'Status')}</th>
                </tr>
              </thead>
              <tbody>
                {actionRows.map((r, i) => (
                  <tr
                    key={`${r.property.id}-${r.controllerLabel}-${r.violation.kind}-${r.violation.program ?? ''}-${i}`}
                    onClick={() => setDetail(r.property)}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-subtle"
                  >
                    <td className="px-4 py-2.5 align-top">
                      <div className="font-medium text-theme-primary">{r.property.name}</div>
                      <div className="text-xs text-theme-secondary">{r.property.city}</div>
                    </td>
                    <td className="px-4 py-2.5 align-top text-theme-secondary">
                      {r.controllerLabel}
                      {r.violation.program && (
                        <span className="ml-1 text-xs text-theme-secondary">
                          {t('complianceReport.programLabel', { program: r.violation.program, defaultValue: `prog {{program}}` })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top text-theme-secondary">
                      <span className="inline-flex items-center gap-1.5">
                        {r.violation.kind === 'day' && <CalendarX size={13} className="shrink-0 text-red-500" />}
                        {r.violation.kind === 'time' && <Clock size={13} className="shrink-0 text-violet-500" />}
                        {r.violation.kind === 'max_days' && <AlertTriangle size={13} className="shrink-0 text-orange-500" />}
                        {problemText(r.violation)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      {r.acknowledged ? (
                        <Pill tone="violet">{t('complianceReport.acknowledged', 'Acknowledged')}</Pill>
                      ) : (
                        <Pill tone="red">{t('complianceReport.unaddressed', 'Unaddressed')}</Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 6. Coverage gaps ------------------------------------------------- */}
      <Card title={t('complianceReport.coverageTitle', 'Coverage gaps — improve data completeness')} icon={<FileWarning size={15} />}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <CoverageBlock
            tone="gray"
            icon={<MapPin size={15} />}
            count={coverage.noScheduleCities.length}
            label={t('complianceReport.gapCitiesLabel', 'Cities with no rule')}
            hint={t('complianceReport.gapCitiesHint', 'No municipal schedule is defined, so none of their properties can be graded.')}
            detail={coverage.noScheduleCities.map((c) => c.city).join(', ')}
          />
          <CoverageBlock
            tone="amber"
            icon={<HelpCircle size={15} />}
            count={coverage.noControllerSchedule.length}
            label={t('complianceReport.gapControllersLabel', 'No controller schedule')}
            hint={t('complianceReport.gapControllersHint', 'Properties have a rule but no controller has a programmed schedule on file to check against.')}
            detail={coverage.noControllerSchedule.map((e) => e.name).slice(0, 8).join(', ')}
          />
          <CoverageBlock
            tone="amber"
            icon={<ShieldQuestion size={15} />}
            count={coverage.undetermined.length}
            label={t('complianceReport.gapUndeterminedLabel', 'Undetermined')}
            hint={t('complianceReport.gapUndeterminedHint', 'Zone/trash-day cities or parity rules without a street number — day compliance is unknown.')}
            detail={coverage.undetermined.map((e) => e.name).slice(0, 8).join(', ')}
          />
        </div>
      </Card>

      {/* 7. Recommendations ----------------------------------------------- */}
      {recommendations.length > 0 && (
        <Card title={t('complianceReport.recTitle', 'Recommended actions')} icon={<Lightbulb size={15} />}>
          <ol className="space-y-2">
            {recommendations.map((r, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-theme-primary">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-500/20 text-xs font-bold text-blue-700 dark:text-blue-300">
                  {i + 1}
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ol>
        </Card>
      )}

      {/* Detail modal (screen only) --------------------------------------- */}
      {detail && (
        <DetailModal
          property={detail}
          onClose={() => setDetail(null)}
          t={t}
          statusLabel={statusLabel}
          statusTone={STATUS_TONE}
          problemText={problemText}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Coverage block — one of the three coverage-gap cells.
// ---------------------------------------------------------------------------
function CoverageBlock({
  tone,
  icon,
  count,
  label,
  hint,
  detail,
}: {
  tone: Tone;
  icon: ReactNode;
  count: number;
  label: string;
  hint: string;
  detail: string;
}) {
  const accent = tone === 'amber' ? '#f59e0b' : '#6b7280';
  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <div className="flex items-center gap-2">
        <span style={{ color: accent }}>{icon}</span>
        <span className="text-2xl font-bold tabular-nums" style={{ color: accent }}>{count}</span>
      </div>
      <div className="mt-1 text-sm font-semibold text-theme-primary">{label}</div>
      <p className="mt-0.5 text-xs text-theme-secondary">{hint}</p>
      {count > 0 && detail && (
        <p className="mt-1.5 truncate text-xs text-theme-secondary" title={detail}>
          {detail}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal — full property compliance breakdown (screen-only overlay).
// ---------------------------------------------------------------------------
function DetailModal({
  property,
  onClose,
  t,
  statusLabel,
  statusTone,
  problemText,
}: {
  property: EvaluatedProperty;
  onClose: () => void;
  t: TFunction<'common'>;
  statusLabel: (s: ComplianceStatus) => string;
  statusTone: Record<ComplianceStatus, Tone>;
  problemText: (v: Violation) => string;
}) {
  const r = property.result;
  const classLabel = property.cls
    ? WATER_CLASS_LABELS[property.cls]
    : t('complianceReport.classUnknown', 'Unknown class');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:hidden"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl border border-line bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="sticky top-0 flex items-start justify-between gap-2 border-b border-line bg-surface px-4 py-3">
          <div>
            <h2 className="text-base font-bold text-theme-primary">{property.name}</h2>
            <p className="text-xs text-theme-secondary">
              {property.city}
              {property.address ? ` · ${property.address}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-theme-secondary hover:bg-subtle"
            aria-label={t('complianceReport.close', 'Close')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 px-4 py-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Pill tone={statusTone[r.status]}>{statusLabel(r.status)}</Pill>
            {r.overridden && <Pill tone="violet">{t('complianceReport.allAcknowledged', 'All overrides acknowledged')}</Pill>}
            <span className="text-theme-secondary">{classLabel}</span>
          </div>

          <div className="rounded-lg bg-sky-50 dark:bg-sky-500/10 px-3 py-2 text-sm">
            <span className="font-semibold text-sky-900 dark:text-sky-200">{t('complianceReport.allowedDays', 'Allowed days')}:</span>{' '}
            <span className="text-sky-800 dark:text-sky-300">{formatDays(r.allowedDays)}</span>
            {r.rule?.no_water_window && (
              <div className="mt-0.5 text-xs text-sky-700 dark:text-sky-300">
                {t('complianceReport.noWaterWindow', 'No watering')}: {r.rule.no_water_window.start}–{r.rule.no_water_window.end}
              </div>
            )}
            {r.rule?.max_days_per_week != null && (
              <div className="mt-0.5 text-xs text-sky-700 dark:text-sky-300">
                {t('complianceReport.maxDays', 'Max days/week')}: {r.rule.max_days_per_week}
              </div>
            )}
          </div>

          <div>
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-theme-secondary">
              {t('complianceReport.controllers', 'Controllers')}
            </h3>
            {r.controllerDetails.length === 0 ? (
              <p className="text-sm text-theme-secondary">{t('complianceReport.noControllerSchedules', 'No programmed controller schedules on file.')}</p>
            ) : (
              <ul className="space-y-2">
                {r.controllerDetails.map((cd, i) => (
                  <li key={`${cd.controllerId}-${i}`} className="rounded-lg border border-line p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-theme-primary">{cd.controllerLabel}</span>
                      {cd.violations.length === 0 ? (
                        <Pill tone="emerald">{t('complianceReport.statusCompliant', 'Compliant')}</Pill>
                      ) : cd.acknowledged ? (
                        <Pill tone="violet">{t('complianceReport.acknowledged', 'Acknowledged')}</Pill>
                      ) : (
                        <Pill tone="red">{t('complianceReport.unaddressed', 'Unaddressed')}</Pill>
                      )}
                    </div>
                    {cd.violations.length > 0 && (
                      <ul className="mt-1.5 space-y-1">
                        {cd.violations.map((v, j) => (
                          <li key={j} className="flex items-start gap-1.5 text-xs text-theme-secondary">
                            {v.kind === 'day' && <CalendarX size={12} className="mt-0.5 shrink-0 text-red-500" />}
                            {v.kind === 'time' && <Clock size={12} className="mt-0.5 shrink-0 text-violet-500" />}
                            {v.kind === 'max_days' && <AlertTriangle size={12} className="mt-0.5 shrink-0 text-orange-500" />}
                            <span>
                              {v.program && (
                                <span className="mr-1 font-medium text-theme-secondary">
                                  {t('complianceReport.programLabel', { program: v.program, defaultValue: 'prog {{program}}' })}
                                </span>
                              )}
                              {problemText(v)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
